import { useIsFetching, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, stripSearchParams } from "@tanstack/react-router";
import { useId } from "react";
import { z } from "zod";

import {
	ACCESS_AREA_LABELS,
	OWNED_PARTY_NOUNS,
	WORK_ITEM_STATES,
	type LinkDocumentWorkItem,
	type WorkItem,
	type WorkItemState,
} from "@ravnar/api-contract";

import { ApiError } from "../api/client";
import { useSession } from "../api/session";
import { workItemQueryOptions, workItemsQueryOptions } from "../api/work";
import { PageHeader } from "../components/shell/app-shell";
import { Button } from "../components/ui/button";
import { buttonClassName } from "../components/ui/button-styles";
import { Card, CardHeader } from "../components/ui/card";
import { Drawer } from "../components/ui/drawer";
import { Empty } from "../components/ui/empty";
import { Pager } from "../components/ui/pager";
import { Badge, Pill } from "../components/ui/pill";
import { Table, type TableColumn } from "../components/ui/table";
import { TabPanel, Tabs } from "../components/ui/tabs";
import { Updating } from "../components/ui/updating";
import { WORK_AREA_DOT_CLASS, workItemAsk, workItemClosing, workStatus } from "../domain/work";
import { cn } from "../lib/cn";
import { formatShortDate } from "../lib/format";
import { CompleteParty } from "./directory/-components/complete-party";
import { PartyLink } from "./directory/-components/party-link";

// `.default()` makes the param optional in links; `.catch()` turns a hand-edited bad value into the fallback.
const workSearchSchema = z.object({
	tab: z.enum(WORK_ITEM_STATES).default("open").catch("open"),
	/** A page link from the API, which the page never reads; the first page when there is none. */
	page: z.string().optional().catch(undefined),
	/** The item the drawer shows. An id the API does not know, or will not show this member, opens nothing. */
	item: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/work")({
	validateSearch: workSearchSchema,
	search: { middlewares: [stripSearchParams({ tab: "open" })] },
	loaderDeps: ({ search: { tab, page } }) => ({ tab, page }),
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: async ({ context, deps }) => {
		try {
			await context.queryClient.query({
				...workItemsQueryOptions({ state: deps.tab, page: deps.page }),
				staleTime: "static",
			});
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the first page.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				throw redirect({ to: "/work", search: { tab: deps.tab }, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "Work · Ravnar" }] }),
	staticData: { crumbs: ["Work"] },
	component: WorkPage,
});

const tabs = [
	{ value: "open", label: "Open" },
	{ value: "done", label: "Done" },
] as const;

/** A timestamp's calendar day in UTC, as the tables show dates. */
const dayOf = (timestamp: string) => formatShortDate(timestamp.slice(0, 10));

/** An item's title, linking to the drawer that shows it, over what it still asks for. */
function ItemCell({ item }: { readonly item: WorkItem }) {
	return (
		<span className="flex items-center gap-2.5">
			<span
				aria-hidden="true"
				className={cn("h-[7px] w-[7px] shrink-0 rounded-full", WORK_AREA_DOT_CLASS[item.area])}
			/>
			<span className="min-w-0">
				<Link
					from="/work"
					to="/work"
					search={previous => ({ ...previous, item: item.id })}
					className="block font-medium text-ink hover:underline"
				>
					{item.title}
				</Link>
				<span className="block text-caption text-ink-3">{workItemAsk(item)}</span>
			</span>
		</span>
	);
}

const statusColumn: TableColumn<WorkItem> = {
	key: "status",
	header: "Status",
	align: "right",
	cell: item => {
		const { tone, label } = workStatus(item);
		return <Pill tone={tone}>{label}</Pill>;
	},
};

const areaColumn: TableColumn<WorkItem> = {
	key: "area",
	header: "Area",
	cell: item => <Badge>{ACCESS_AREA_LABELS[item.area]}</Badge>,
};

/** Open work: when it was raised and by whom, oldest first. */
const openColumns: readonly TableColumn<WorkItem>[] = [
	{ key: "item", header: "Item", cell: item => <ItemCell item={item} /> },
	areaColumn,
	{ key: "raised", header: "Raised", align: "right", muted: true, cell: item => dayOf(item.createdAt) },
	{ key: "by", header: "By", muted: true, cell: item => item.createdBy },
	statusColumn,
];

/** Done work: when it was closed and by whom, most recent first. */
const doneColumns: readonly TableColumn<WorkItem>[] = [
	{ key: "item", header: "Item", cell: item => <ItemCell item={item} /> },
	areaColumn,
	{
		key: "closed",
		header: "Closed",
		align: "right",
		muted: true,
		cell: item => dayOf(item.closedAt ?? item.createdAt),
	},
	{ key: "by", header: "By", muted: true, cell: item => item.closedBy ?? "—" },
	statusColumn,
];

/**
 * A link to the page of the document an item asks to link, which any member may open — with the drawer that links it
 * already open (`?link=true`) when `toLink` says the member is there to link it.
 */
function DocumentPageLink({
	document,
	toLink = false,
	className = "font-medium text-accent hover:underline",
}: {
	readonly document: LinkDocumentWorkItem["document"];
	readonly toLink?: boolean;
	readonly className?: string;
}) {
	return (
		<Link
			to="/intake/$fileId"
			params={{ fileId: document.fileId }}
			search={toLink ? { link: true } : {}}
			className={className}
		>
			Open {document.fileName}
		</Link>
	);
}

/** Where a done item leads: the party it completed, or the document it linked; nowhere once its party is gone. */
function ClosedItemLink({ item }: { readonly item: WorkItem }) {
	if (item.kind === "link-document") {
		return <DocumentPageLink document={item.document} />;
	}
	return item.resolution === "removed" ? null : (
		<PartyLink kind={item.party.kind} id={item.party.id} className="font-medium text-accent hover:underline">
			Open {item.party.name}
		</PartyLink>
	);
}

interface WorkItemDrawerProps {
	/** The id the URL names, if any; the drawer opens once the API has answered with the item. */
	readonly itemId: string | undefined;
	readonly onClose: () => void;
}

/**
 * One item in the drawer: what it asks and, while it is open, what to do it with. A party to complete comes with its
 * own form, and saving the form completes the party, which closes the item. A document to link comes with the way to
 * its page, where it is linked, and the first link closes the item. There is no other way to close one.
 */
function WorkItemDrawer({ itemId, onClose }: WorkItemDrawerProps) {
	const { member } = useSession();
	const formId = useId();
	// Asked without suspending: an item this member may not see answers 404, and then the drawer simply stays shut.
	const { data: item } = useQuery({ ...workItemQueryOptions(itemId ?? ""), enabled: itemId !== undefined });
	const shown = itemId !== undefined && item?.id === itemId ? item : undefined;
	const canEdit = shown !== undefined && member.access[shown.area] === "editor";
	// Only a party is done in the drawer itself; a document is linked on its own page.
	const completing = canEdit && shown.kind === "complete-party" && shown.state === "open" ? shown : undefined;
	const status = shown === undefined ? undefined : workStatus(shown);

	return (
		<Drawer
			open={shown !== undefined}
			title={shown?.title ?? ""}
			onClose={onClose}
			// Only somebody who can complete the party gets a footer: everybody else closes the drawer from its header.
			footer={
				completing === undefined ? undefined : (
					<>
						<Button onClick={onClose}>Cancel</Button>
						<Button type="submit" form={formId} variant="primary">
							Save {OWNED_PARTY_NOUNS[completing.party.kind]}
						</Button>
					</>
				)
			}
		>
			{shown === undefined || status === undefined ? null : (
				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-2">
						<Pill tone={status.tone}>{status.label}</Pill>
						<Badge>{ACCESS_AREA_LABELS[shown.area]}</Badge>
						<span className="ml-auto text-caption text-ink-3 tabular-nums">
							Raised {dayOf(shown.createdAt)} by {shown.createdBy}
						</span>
					</div>
					<p className="text-body text-ink-2">{shown.detail}</p>
					{shown.state === "open" ? (
						<>
							{canEdit ? null : (
								<p className="text-caption text-ink-3">
									Only an editor of the {ACCESS_AREA_LABELS[shown.area]} can{" "}
									{shown.kind === "complete-party" ? "complete" : "link"} it.
								</p>
							)}
							{shown.kind === "complete-party" ? (
								<CompleteParty item={shown} formId={formId} canEdit={canEdit} onCompleted={onClose} />
							) : (
								// Any member may open a document, so a reader is shown the way to it as well; an editor
								// lands on it with the drawer that links it open.
								<DocumentPageLink
									document={shown.document}
									toLink={canEdit}
									className={buttonClassName({ variant: "primary" }, "self-start")}
								/>
							)}
						</>
					) : (
						<p className="text-body text-ink-2">
							{workItemClosing(shown)}. <ClosedItemLink item={shown} />
						</p>
					)}
				</div>
			)}
		</Drawer>
	);
}

/** What the card says when a tab has nothing on it. */
function nothingOn(tab: WorkItemState): string {
	return tab === "open" ? "Nothing to do. Work the agents raise lands here." : "Nothing done yet.";
}

function WorkPage() {
	const { tab, page, item } = Route.useSearch();
	const navigate = Route.useNavigate();
	const tabsId = useId();
	const workQuery = workItemsQueryOptions({ state: tab, page });
	const { data: shown } = useSuspenseQuery(workQuery);
	const isUpdating = useIsFetching({ queryKey: workQuery.queryKey, exact: true }) > 0;

	const closeDrawer = () => {
		void navigate({ search: previous => ({ ...previous, item: undefined }), replace: true });
	};

	return (
		<>
			<PageHeader
				title="Work"
				description="What needs doing across the business, oldest first. An item closes itself once the work is done."
			/>
			<div className="mb-3.5">
				<Tabs
					id={tabsId}
					label="Work"
					items={tabs}
					value={tab}
					onChange={value => {
						// The other tab pages on its own, so this one's page link is left behind.
						void navigate({ search: { tab: value }, replace: true });
					}}
				/>
			</div>
			<TabPanel id={tabsId} value={tab}>
				<Card>
					<CardHeader title={`${shown.total} ${tab}`} action={<Updating active={isUpdating} />} />
					{shown.items.length > 0 ? (
						<Table
							label={tab === "open" ? "Open work" : "Done work"}
							columns={tab === "open" ? openColumns : doneColumns}
							rows={shown.items}
							rowKey={row => row.id}
							interactiveRows
						/>
					) : (
						<Empty>{shown.total > 0 ? "Nothing is left on this page." : nothingOn(tab)}</Empty>
					)}
					{shown.next === null && shown.previous === null ? null : (
						<Pager
							label="Work pages"
							previous={
								shown.previous === null ? null : (
									<Link
										to="/work"
										search={{ tab, page: shown.previous }}
										className={buttonClassName({ size: "sm" })}
									>
										Previous
									</Link>
								)
							}
							next={
								shown.next === null ? null : (
									<Link
										to="/work"
										search={{ tab, page: shown.next }}
										className={buttonClassName({ size: "sm" })}
									>
										Next
									</Link>
								)
							}
						/>
					)}
				</Card>
			</TabPanel>
			<WorkItemDrawer itemId={item} onClose={closeDrawer} />
		</>
	);
}
